import {
  AlertCircle,
  Award,
  BookOpen,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock,
  DollarSign,
  GraduationCap,
  MapPin,
  RefreshCw,
  Search,
  Star,
  Tag,
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { ServiceBanner } from '@/components/ServiceBanner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useBanner } from '@/hooks/useBanner'
import { trackEvent } from '@/lib/analytics'

interface EducationItem {
  title: string
  description: string
  url: string
  imageUrl?: string
  eventDate: string
  endDate?: string
  eventTime: string
  location: string
  category: string
  registrationWarning?: string
  registrationClosed?: boolean
  trainingType?: string
  cpdPoints?: string
  priceRange?: string
  disclaimer?: string
}

interface EducationScreenProps {
  onBack: () => void
  partyId?: string
  initialTab?: string
}

const PAGE_SIZE = 10
const CACHE_TTL = 30 * 60 * 1000

function getCached<T>(key: string): T | null {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return null
    const { data, fetchedAt, ttl } = JSON.parse(raw)
    if (Date.now() - fetchedAt >= ttl) return null
    return data as T
  } catch {
    return null
  }
}

function setCached<T>(key: string, data: T, ttl: number): void {
  try {
    localStorage.setItem(key, JSON.stringify({ data, fetchedAt: Date.now(), ttl }))
  } catch {}
}

function useItemList(cacheKey: string, apiPath: string) {
  const [items, setItems] = useState<EducationItem[]>([])
  const [loading, setLoading] = useState(true)

  const load = (bust = false) => {
    if (!bust) {
      const cached = getCached<EducationItem[]>(cacheKey)
      if (cached) {
        setItems(cached)
        setLoading(false)
        return
      }
    }
    setLoading(true)
    localStorage.removeItem(cacheKey)
    fetch(apiPath)
      .then((r) => r.json())
      .then((data) => {
        setItems(data)
        setCached(cacheKey, data, CACHE_TTL)
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    load()
  }, [])
  return { items, loading, reload: load }
}

function CourseList({
  items,
  loading,
  savedUrls,
  onToggleSave,
  registeredKeys,
}: {
  items: EducationItem[]
  loading: boolean
  savedUrls: Set<string>
  onToggleSave: (item: EducationItem) => void
  registeredKeys: Set<string>
}) {
  const [search, setSearch] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('All')
  const [selectedYear, setSelectedYear] = useState('All')
  const [selectedLevel, setSelectedLevel] = useState('All')
  const [page, setPage] = useState(1)
  const listRef = useRef<HTMLDivElement>(null)

  const extractLevel = (title: string) => {
    const m = title.match(/Level\s+(\d)/i)
    return m ? `Level ${m[1]}` : null
  }

  const getLevelBadge = (level: string) => {
    const n = parseInt(level.replace(/\D/g, ''))
    if (n === 4) return 'bg-blue-100 text-blue-700'
    if (n === 5) return 'bg-green-100 text-green-700'
    if (n === 6) return 'bg-orange-100 text-orange-700'
    return 'bg-purple-100 text-purple-700'
  }

  const categories = useMemo(() => {
    const unique = Array.from(new Set(items.map((e) => e.category).filter(Boolean)))
    return ['All', ...unique.sort()]
  }, [items])

  const years = useMemo(() => {
    const unique = Array.from(
      new Set(items.map((e) => e.eventDate?.split(' ')[2]).filter(Boolean)),
    ) as string[]
    return ['All', ...unique.sort().reverse()]
  }, [items])

  const levels = useMemo(() => {
    const unique = Array.from(
      new Set(items.map((e) => extractLevel(e.title)).filter(Boolean)),
    ) as string[]
    return ['All', ...unique.sort()]
  }, [items])

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim()
    return items.filter((e) => {
      const matchesCategory = selectedCategory === 'All' || e.category === selectedCategory
      const matchesYear = selectedYear === 'All' || e.eventDate?.includes(selectedYear)
      const matchesLevel = selectedLevel === 'All' || extractLevel(e.title) === selectedLevel
      const matchesSearch =
        !q || e.title.toLowerCase().includes(q) || e.description.toLowerCase().includes(q)
      return matchesCategory && matchesYear && matchesLevel && matchesSearch
    })
  }, [items, search, selectedCategory, selectedYear, selectedLevel])

  const sorted = filtered

  const onlineCount = useMemo(
    () => filtered.filter((e) => e.category?.toLowerCase() === 'online').length,
    [filtered],
  )
  const f2fCount = useMemo(
    () => filtered.filter((e) => e.category?.toLowerCase().includes('face')).length,
    [filtered],
  )

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const paginated = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)
  const resetPage = () => setPage(1)

  return (
    <>
      {/* Search + Level + Year */}
      <div className="flex gap-2 mb-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search courses..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              resetPage()
            }}
            className="pl-9 text-sm"
          />
        </div>
        {levels.length > 2 && (
          <select
            value={selectedLevel}
            onChange={(e) => {
              setSelectedLevel(e.target.value)
              resetPage()
            }}
            className="text-xs border border-border rounded-lg px-2 py-1.5 bg-background text-foreground focus:outline-hidden focus:ring-1 focus:ring-primary"
          >
            {levels.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </select>
        )}
        {years.length > 2 && (
          <select
            value={selectedYear}
            onChange={(e) => {
              setSelectedYear(e.target.value)
              resetPage()
            }}
            className="text-xs border border-border rounded-lg px-2 py-1.5 bg-background text-foreground focus:outline-hidden focus:ring-1 focus:ring-primary"
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Category filter + summary */}
      {!loading && (
        <div className="flex items-center gap-2 mb-3">
          <div className="flex gap-1.5 overflow-x-auto scrollbar-hide flex-1">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => {
                  setSelectedCategory(cat)
                  resetPage()
                }}
                className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${selectedCategory === cat ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
              >
                {cat}
              </button>
            ))}
          </div>
          {filtered.length > 0 && (
            <span className="shrink-0 text-3xs text-muted-foreground">
              {filtered.length} course{filtered.length !== 1 ? 's' : ''}
              {onlineCount > 0 && f2fCount > 0 && (
                <span className="ml-1">
                  · {onlineCount} online · {f2fCount} F2F
                </span>
              )}
            </span>
          )}
        </div>
      )}

      {loading && (
        <div className="flex flex-col gap-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="rounded-xl bg-muted animate-pulse h-28" />
          ))}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center mt-10 gap-3 text-center">
          <GraduationCap className="w-10 h-10 text-muted-foreground/40" />
          <p className="text-sm font-medium text-foreground">No courses found</p>
        </div>
      )}

      {!loading && paginated.length > 0 && (
        <div ref={listRef} className="flex flex-col gap-3">
          {paginated.map((item, i) => {
            const level = extractLevel(item.title)
            const isSaved = savedUrls.has(item.url || item.title)
            const eventKey = item.url?.match(/EventKey=([^&]+)/i)?.[1]?.toLowerCase() ?? ''
            const isEnrolled = eventKey ? registeredKeys.has(eventKey) : false
            return (
              <div key={i} className="rounded-xl overflow-hidden border border-border bg-card">
                <div className="relative h-16 bg-linear-to-r from-primary to-primary/70 flex items-center justify-center">
                  <GraduationCap className="w-7 h-7 text-primary-foreground/40" />
                  {item.eventDate && (
                    <div className="absolute bottom-2 left-3 bg-white/20 text-white text-xs font-bold px-2 py-1 rounded-md">
                      {item.eventDate}
                    </div>
                  )}
                  <button
                    onClick={() => onToggleSave(item)}
                    className="absolute top-2 right-2 p-1.5 rounded-full bg-black/20 hover:bg-black/40 transition-colors"
                  >
                    <Star
                      className={`w-4 h-4 ${isSaved ? 'fill-yellow-400 text-yellow-400' : 'text-white'}`}
                    />
                  </button>
                </div>
                <div className="p-3">
                  {item.registrationWarning && (
                    <div
                      className={`flex items-center gap-1 text-3xs font-medium rounded px-2 py-1 mb-2 ${item.registrationClosed ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'}`}
                    >
                      <AlertCircle className="w-3 h-3 shrink-0" />
                      {item.registrationWarning}
                    </div>
                  )}
                  {/* Badges */}
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {level && (
                      <span
                        className={`inline-flex items-center text-3xs font-semibold px-2 py-0.5 rounded-full ${getLevelBadge(level)}`}
                      >
                        {level}
                      </span>
                    )}
                    {item.category && (
                      <span className="inline-flex items-center gap-1 text-3xs font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                        <Tag className="w-3 h-3" />
                        {item.category}
                      </span>
                    )}
                  </div>
                  <p className="text-sm font-semibold text-foreground line-clamp-2 mb-2">
                    {item.title}
                  </p>
                  <div className="flex flex-col gap-1 mb-2">
                    {(item.eventDate || item.endDate) && (
                      <span className="flex items-center gap-1.5 text-3xs text-muted-foreground">
                        <Calendar className="w-3 h-3 shrink-0" />
                        {item.eventDate}
                        {item.endDate ? ` – ${item.endDate}` : ''}
                      </span>
                    )}
                    {item.eventTime && (
                      <span className="flex items-center gap-1.5 text-3xs text-muted-foreground">
                        <Clock className="w-3 h-3 shrink-0" />
                        {item.eventTime}
                      </span>
                    )}
                    {item.location && (
                      <span className="flex items-center gap-1.5 text-3xs text-muted-foreground min-w-0">
                        <MapPin className="w-3 h-3 shrink-0" />
                        <span className="truncate">{item.location}</span>
                      </span>
                    )}
                    {item.priceRange && (
                      <span className="flex items-center gap-1.5 text-3xs text-muted-foreground">
                        <DollarSign className="w-3 h-3 shrink-0" />
                        {item.priceRange}
                      </span>
                    )}
                  </div>
                  {item.description && (
                    <p className="text-3xs text-muted-foreground mb-3 line-clamp-2">
                      {item.description}
                    </p>
                  )}
                  {item.disclaimer && (
                    <div className="border-l-2 border-primary/50 bg-primary/5 px-2 py-1.5 mb-2 rounded-r">
                      <p className="text-3xs text-foreground font-medium mb-0.5">
                        Important Disclaimer
                      </p>
                      <p className="text-3xs text-muted-foreground line-clamp-3">
                        {item.disclaimer}
                      </p>
                    </div>
                  )}
                  {item.url &&
                    (isEnrolled ? (
                      <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs font-medium px-3 py-1.5 rounded-lg">
                        ✓ Enrolled
                      </span>
                    ) : item.registrationClosed ? (
                      <span className="inline-flex items-center gap-1 bg-muted text-muted-foreground text-xs font-medium px-3 py-1.5 rounded-lg cursor-not-allowed">
                        Registration Closed
                      </span>
                    ) : (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackEvent('enrol_clicked', { course: item.title })}
                        className="inline-flex items-center gap-1 bg-primary text-primary-foreground text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-primary/90 transition-colors"
                      >
                        Enrol <ChevronRight className="w-3 h-3" />
                      </a>
                    ))}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setPage(Math.max(1, currentPage - 1))
              listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }}
            disabled={currentPage === 1}
            className="flex items-center gap-1 text-xs"
          >
            <ChevronLeft className="w-3 h-3" /> Previous
          </Button>
          <span className="text-xs text-muted-foreground">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setPage(Math.min(totalPages, currentPage + 1))
              listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }}
            disabled={currentPage === totalPages}
            className="flex items-center gap-1 text-xs"
          >
            Next <ChevronRight className="w-3 h-3" />
          </Button>
        </div>
      )}
    </>
  )
}

function CPDList({
  items,
  loading,
  savedUrls,
  onToggleSave,
  registeredKeys,
}: {
  items: EducationItem[]
  loading: boolean
  savedUrls: Set<string>
  onToggleSave: (item: EducationItem) => void
  registeredKeys: Set<string>
}) {
  const [search, setSearch] = useState('')
  const [selectedType, setSelectedType] = useState('All')
  const [selectedYear, setSelectedYear] = useState('All')
  const [sortBy, setSortBy] = useState<'date' | 'title' | 'cpd'>('date')
  const [page, setPage] = useState(1)
  const listRef = useRef<HTMLDivElement>(null)

  const isMandatory = (desc: string) => desc?.includes('[MANDATORY]')
  const cleanDescription = (desc: string) => desc?.replace(/\[MANDATORY\]\s*/i, '').trim()

  const trainingTypes = useMemo(() => {
    const unique = Array.from(new Set(items.map((e) => e.trainingType).filter(Boolean))) as string[]
    return ['All', ...unique.sort()]
  }, [items])

  const years = useMemo(() => {
    const unique = Array.from(
      new Set(items.map((e) => e.eventDate?.split(' ')[2]).filter(Boolean)),
    ) as string[]
    return ['All', ...unique.sort().reverse()]
  }, [items])

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim()
    return items.filter((e) => {
      const matchesType = selectedType === 'All' || e.trainingType === selectedType
      const matchesYear = selectedYear === 'All' || e.eventDate?.includes(selectedYear)
      const matchesSearch =
        !q || e.title.toLowerCase().includes(q) || e.description?.toLowerCase().includes(q)
      return matchesType && matchesYear && matchesSearch
    })
  }, [items, search, selectedType, selectedYear])

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      if (sortBy === 'title') return a.title.localeCompare(b.title)
      if (sortBy === 'cpd')
        return (parseFloat(b.cpdPoints ?? '0') || 0) - (parseFloat(a.cpdPoints ?? '0') || 0)
      return 0 // date — already sorted by backend
    })
  }, [filtered, sortBy])

  const totalCpdHours = useMemo(
    () => filtered.reduce((sum, e) => sum + (parseFloat(e.cpdPoints ?? '0') || 0), 0),
    [filtered],
  )

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE))
  const currentPage = Math.min(page, totalPages)
  const paginated = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE)

  const getTrainingTypeBadge = (type: string) => {
    const isVerifiable =
      type.toLowerCase().includes('verifiable') && !type.toLowerCase().includes('non')
    return isVerifiable ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
  }

  const resetPage = () => setPage(1)

  return (
    <>
      {/* Search + Sort dropdown */}
      <div className="flex gap-2 mb-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search CPD..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              resetPage()
            }}
            className="pl-9 text-sm"
          />
        </div>
        <select
          value={sortBy}
          onChange={(e) => {
            setSortBy(e.target.value as 'date' | 'title' | 'cpd')
            resetPage()
          }}
          className="text-xs border border-border rounded-lg px-2 py-1.5 bg-background text-foreground focus:outline-hidden focus:ring-1 focus:ring-primary"
        >
          <option value="date">Date</option>
          <option value="title">Title A–Z</option>
          <option value="cpd">CPD Hours</option>
        </select>
        {years.length > 2 && (
          <select
            value={selectedYear}
            onChange={(e) => {
              setSelectedYear(e.target.value)
              resetPage()
            }}
            className="text-xs border border-border rounded-lg px-2 py-1.5 bg-background text-foreground focus:outline-hidden focus:ring-1 focus:ring-primary"
          >
            {years.map((y) => (
              <option key={y} value={y}>
                {y}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Training type filter + summary */}
      {!loading && (
        <div className="flex items-center gap-2 mb-3">
          <div className="flex gap-1.5 overflow-x-auto scrollbar-hide flex-1">
            {trainingTypes.map((type) => (
              <button
                key={type}
                onClick={() => {
                  setSelectedType(type)
                  resetPage()
                }}
                className={`shrink-0 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${selectedType === type ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:bg-muted/80'}`}
              >
                {type}
              </button>
            ))}
          </div>
          {filtered.length > 0 && (
            <span className="shrink-0 text-3xs text-muted-foreground">
              {filtered.length} session{filtered.length !== 1 ? 's' : ''}
              {totalCpdHours > 0 && (
                <span className="ml-1 font-semibold text-purple-700">· {totalCpdHours} hrs</span>
              )}
            </span>
          )}
        </div>
      )}

      {loading && (
        <div className="flex flex-col gap-3">
          {[...Array(5)].map((_, i) => (
            <div key={i} className="rounded-xl bg-muted animate-pulse h-36" />
          ))}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="flex flex-col items-center justify-center mt-10 gap-3 text-center">
          <BookOpen className="w-10 h-10 text-muted-foreground/40" />
          <p className="text-sm font-medium text-foreground">No CPD sessions found</p>
        </div>
      )}

      {!loading && paginated.length > 0 && (
        <div ref={listRef} className="flex flex-col gap-3">
          {paginated.map((item, i) => {
            const mandatory = isMandatory(item.description ?? '')
            const desc = cleanDescription(item.description ?? '')
            const isSaved = savedUrls.has(item.url || item.title)
            const eventKey = item.url?.match(/EventKey=([^&]+)/i)?.[1]?.toLowerCase() ?? ''
            const isEnrolled = eventKey ? registeredKeys.has(eventKey) : false
            return (
              <div
                key={i}
                className="rounded-xl border border-border bg-card overflow-hidden relative"
              >
                <div
                  className={`h-1.5 bg-linear-to-r ${mandatory ? 'from-red-500 to-red-400' : 'from-primary to-primary/50'}`}
                />
                <button
                  onClick={() => onToggleSave(item)}
                  className="absolute top-2 right-2 p-1.5 rounded-full hover:bg-muted/80 transition-colors z-10"
                >
                  <Star
                    className={`w-4 h-4 ${isSaved ? 'fill-yellow-400 text-yellow-400' : 'text-muted-foreground'}`}
                  />
                </button>
                <div className="p-3">
                  {/* Badges */}
                  <div className="flex flex-wrap gap-1.5 mb-2">
                    {mandatory && (
                      <span className="inline-flex items-center gap-1 text-3xs font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                        Mandatory
                      </span>
                    )}
                    {item.trainingType && (
                      <span
                        className={`inline-flex items-center gap-1 text-3xs font-semibold px-2 py-0.5 rounded-full ${getTrainingTypeBadge(item.trainingType)}`}
                      >
                        <Award className="w-3 h-3" />
                        {item.trainingType}
                      </span>
                    )}
                    {item.cpdPoints && (
                      <span className="inline-flex items-center gap-1 text-3xs font-semibold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                        <BookOpen className="w-3 h-3" />
                        {item.cpdPoints} CPD {parseInt(item.cpdPoints) === 1 ? 'Hour' : 'Hours'}
                      </span>
                    )}
                    {item.category && (
                      <span className="inline-flex items-center gap-1 text-3xs font-medium px-2 py-0.5 rounded-full bg-muted text-muted-foreground">
                        <Tag className="w-3 h-3" />
                        {item.category}
                      </span>
                    )}
                  </div>

                  {/* Registration warning */}
                  {item.registrationWarning && (
                    <div
                      className={`flex items-center gap-1 text-3xs font-medium rounded px-2 py-1 mb-2 ${item.registrationClosed ? 'bg-red-50 text-red-600' : 'bg-amber-50 text-amber-600'}`}
                    >
                      <AlertCircle className="w-3 h-3 shrink-0" />
                      {item.registrationWarning}
                    </div>
                  )}

                  <p className="text-sm font-semibold text-foreground line-clamp-2 mb-2">
                    {item.title}
                  </p>

                  <div className="flex flex-col gap-1 mb-2">
                    {(item.eventDate || item.endDate) && (
                      <span className="flex items-center gap-1.5 text-3xs text-muted-foreground">
                        <Calendar className="w-3 h-3 shrink-0" />
                        {item.eventDate}
                        {item.endDate ? ` – ${item.endDate}` : ''}
                      </span>
                    )}
                    {item.eventTime && (
                      <span className="flex items-center gap-1.5 text-3xs text-muted-foreground">
                        <Clock className="w-3 h-3 shrink-0" />
                        {item.eventTime}
                      </span>
                    )}
                    {item.location && (
                      <span className="flex items-center gap-1.5 text-3xs text-muted-foreground min-w-0">
                        <MapPin className="w-3 h-3 shrink-0" />
                        <span className="truncate">{item.location}</span>
                      </span>
                    )}
                    {item.priceRange && (
                      <span className="flex items-center gap-1.5 text-3xs text-muted-foreground">
                        <DollarSign className="w-3 h-3 shrink-0" />
                        {item.priceRange}
                      </span>
                    )}
                  </div>

                  {desc && (
                    <p className="text-3xs text-muted-foreground mb-2 line-clamp-2">{desc}</p>
                  )}

                  {item.disclaimer && (
                    <div className="border-l-2 border-primary/50 bg-primary/5 px-2 py-1.5 mb-2 rounded-r">
                      <p className="text-3xs text-foreground font-medium mb-0.5">
                        Important Disclaimer
                      </p>
                      <p className="text-3xs text-muted-foreground line-clamp-3">
                        {item.disclaimer}
                      </p>
                    </div>
                  )}

                  {item.url &&
                    (isEnrolled ? (
                      <span className="inline-flex items-center gap-1 bg-green-100 text-green-700 text-xs font-medium px-3 py-1.5 rounded-lg">
                        ✓ Registered
                      </span>
                    ) : item.registrationClosed ? (
                      <span className="inline-flex items-center gap-1 bg-muted text-muted-foreground text-xs font-medium px-3 py-1.5 rounded-lg cursor-not-allowed">
                        Registration Closed
                      </span>
                    ) : (
                      <a
                        href={item.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => trackEvent('register_clicked', { course: item.title })}
                        className="inline-flex items-center gap-1 bg-primary text-primary-foreground text-xs font-medium px-3 py-1.5 rounded-lg hover:bg-primary/90 transition-colors"
                      >
                        Register <ChevronRight className="w-3 h-3" />
                      </a>
                    ))}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setPage(Math.max(1, currentPage - 1))
              listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }}
            disabled={currentPage === 1}
            className="flex items-center gap-1 text-xs"
          >
            <ChevronLeft className="w-3 h-3" /> Previous
          </Button>
          <span className="text-xs text-muted-foreground">
            Page {currentPage} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setPage(Math.min(totalPages, currentPage + 1))
              listRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
            }}
            disabled={currentPage === totalPages}
            className="flex items-center gap-1 text-xs"
          >
            Next <ChevronRight className="w-3 h-3" />
          </Button>
        </div>
      )}
    </>
  )
}

export function EducationScreen({ onBack, partyId, initialTab }: EducationScreenProps) {
  const banner = useBanner('education')
  useEffect(() => {
    trackEvent('screen_viewed', { screen: 'education' })
  }, [])
  const courses = useItemList('reinz_education', 'api/events/education')
  const cpd = useItemList('reinz_cpd', 'api/events/cpd')
  const isLoading = courses.loading || cpd.loading

  const [savedUrls, setSavedUrls] = useState<Set<string>>(new Set())
  const [savedItems, setSavedItems] = useState<EducationItem[]>([])
  const [registeredKeys, setRegisteredKeys] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (!partyId) return
    fetch(`api/events/registrations?partyId=${partyId}`)
      .then((r) => r.json())
      .then((keys: string[]) => setRegisteredKeys(new Set(keys.map((k) => k.toLowerCase()))))
      .catch(() => {})
  }, [partyId])

  useEffect(() => {
    if (!partyId) return
    fetch(`api/user-saved-items?userId=${encodeURIComponent(partyId)}&itemType=education`)
      .then((r) => r.json())
      .then((items: any[]) => {
        const parsed = items.map((i) => JSON.parse(i.itemData) as EducationItem)
        setSavedItems(parsed)
        setSavedUrls(new Set(parsed.map((e) => e.url || e.title)))
      })
      .catch(() => {})
  }, [partyId])

  const handleToggleSave = async (item: EducationItem) => {
    if (!partyId) return
    const key = item.url || item.title
    const isSaved = savedUrls.has(key)
    try {
      if (isSaved) {
        const r = await fetch(
          `api/user-saved-items?userId=${encodeURIComponent(partyId)}&itemType=education&itemId=${encodeURIComponent(key)}`,
          { method: 'DELETE' },
        )
        if (r.ok) {
          setSavedUrls((prev) => {
            const s = new Set(prev)
            s.delete(key)
            return s
          })
          setSavedItems((prev) => prev.filter((e) => (e.url || e.title) !== key))
        }
      } else {
        const r = await fetch('api/user-saved-items', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userId: partyId,
            itemType: 'education',
            itemId: key,
            itemData: JSON.stringify(item),
          }),
        })
        if (r.ok) {
          setSavedUrls((prev) => new Set([...prev, key]))
          setSavedItems((prev) => [item, ...prev])
          trackEvent('item_saved', { itemType: 'education', itemId: key })
        }
      }
    } catch {
      /* network error — leave UI state unchanged */
    }
  }

  return (
    <>
      <ServiceBanner banner={banner} />
      <Tabs defaultValue={initialTab ?? 'courses'}>
        {/* Sticky header */}
        <div className="sticky top-0 z-30 bg-background px-4 pb-2">
          <div className="flex items-center gap-2 mb-2">
            <Button variant="ghost" size="icon" onClick={onBack} className="h-8 w-8">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-lg font-bold text-foreground flex-1 font-heading">
              Education & Training
            </h1>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => {
                courses.reload(true)
                cpd.reload(true)
              }}
              disabled={isLoading}
              className="h-8 w-8"
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          <TabsList className="w-full grid grid-cols-3 mb-0">
            <TabsTrigger value="courses" className="flex items-center gap-1.5 text-xs">
              <GraduationCap className="w-3.5 h-3.5" />
              Courses
            </TabsTrigger>
            <TabsTrigger value="cpd" className="flex items-center gap-1.5 text-xs">
              <BookOpen className="w-3.5 h-3.5" />
              CPD
            </TabsTrigger>
            <TabsTrigger value="saved" className="flex items-center gap-1.5 text-xs">
              <Star className="w-3.5 h-3.5" />
              Saved {savedItems.length > 0 && `(${savedItems.length})`}
            </TabsTrigger>
          </TabsList>
        </div>

        <div className="px-4 pb-20">
          <TabsContent value="courses">
            <CourseList
              items={courses.items}
              loading={courses.loading}
              savedUrls={savedUrls}
              onToggleSave={handleToggleSave}
              registeredKeys={registeredKeys}
            />
          </TabsContent>

          <TabsContent value="cpd">
            <CPDList
              items={cpd.items}
              loading={cpd.loading}
              savedUrls={savedUrls}
              onToggleSave={handleToggleSave}
              registeredKeys={registeredKeys}
            />
          </TabsContent>

          <TabsContent value="saved">
            {savedItems.length === 0 ? (
              <div className="flex flex-col items-center justify-center mt-10 gap-3 text-center">
                <Star className="w-10 h-10 text-muted-foreground/40" />
                <p className="text-sm font-medium text-foreground">No saved items yet</p>
                <p className="text-xs text-muted-foreground">
                  Tap the ★ on any course or CPD session to save it here
                </p>
              </div>
            ) : (
              <div className="flex flex-col divide-y divide-border rounded-xl border border-border overflow-hidden">
                {savedItems.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 p-3 bg-card hover:bg-muted/30 transition-colors"
                  >
                    {/* Date badge */}
                    <div className="shrink-0 w-12 text-center bg-primary/10 rounded-lg p-1.5">
                      <p className="text-xs font-bold text-primary leading-tight">
                        {item.eventDate?.split(' ')[0] ?? '—'}
                      </p>
                      <p className="text-3xs text-primary/70 uppercase leading-tight">
                        {item.eventDate?.split(' ')[1] ?? ''}
                      </p>
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        {item.cpdPoints ? (
                          <span className="inline-flex items-center text-3xs font-semibold px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-700">
                            CPD
                          </span>
                        ) : (
                          <span className="inline-flex items-center text-3xs font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-700">
                            Course
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-semibold text-foreground line-clamp-1">
                        {item.title}
                      </p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        {item.location && (
                          <span className="flex items-center gap-1 text-3xs text-muted-foreground min-w-0">
                            <MapPin className="w-3 h-3 shrink-0" />
                            <span className="truncate">{item.location}</span>
                          </span>
                        )}
                        {item.cpdPoints && (
                          <span className="flex items-center gap-1 text-3xs text-purple-700 font-medium">
                            <Award className="w-3 h-3" />
                            {item.cpdPoints} hrs
                          </span>
                        )}
                      </div>
                      {item.url &&
                        (item.registrationClosed ? (
                          <span className="text-3xs text-muted-foreground mt-0.5 inline-block">
                            Registration Closed
                          </span>
                        ) : (
                          <a
                            href={item.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() =>
                              trackEvent(item.cpdPoints ? 'register_clicked' : 'enrol_clicked', {
                                course: item.title,
                              })
                            }
                            className="text-3xs text-primary hover:underline mt-0.5 inline-block"
                          >
                            {item.cpdPoints ? 'Register →' : 'Enrol →'}
                          </a>
                        ))}
                    </div>

                    {/* Unsave */}
                    <button
                      onClick={() => handleToggleSave(item)}
                      className="shrink-0 p-1.5 rounded-full hover:bg-muted transition-colors"
                      title="Remove from saved"
                    >
                      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>
        </div>
      </Tabs>
    </>
  )
}
